import type { ReactNode } from "react";

import type { CableRowResponse, CableTrayResponse } from "../services/cableTray";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  ALLOWANCE_BASIS_LABELS,
  ARRANGEMENT_LABELS,
  COVER_LABELS,
  FILL_BASIS_LABELS,
  LOAD_BASIS_LABELS,
  RATED_LOAD_BASIS_LABELS,
  TRAY_TYPE_LABELS,
} from "./cableTrayLabels";
import { STATUS_LABELS, rowKind, statusHeading } from "./CableTrayResultSummary";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";

interface CableTrayResultPanelProps {
  result: CableTrayResponse;
}

/** Carried by every result (decisions D1, D5, D6): what the check is and what it is not. */
export const METHOD_NOTE =
  "The fill is the sum of the circumscribed circles of the declared overall diameters " +
  "(multi-layer) or the sum of the diameters and the declared spacings (single layer), scaled by " +
  "the declared future allowance (the width only when the allowance's basis names it); the load " +
  "is the declared mass per metre with the same allowance against the declared rating at its " +
  "span. Segregation, the cover's thermal effect, the earth continuity, the bend radii and the " +
  "tray's mechanical design are not checked here; the sheet says so on every route. Nothing of " +
  "the linked cable runs is recalculated.";

export const RESULT_WORDS = {
  met: "Met",
  notMet: "Not met",
  notCompared: "Not compared",
} as const;

function resultKey(met: boolean | null): string {
  if (met === null) {
    return "not-evaluated";
  }
  return met ? "pass" : "fail";
}

function resultWord(met: boolean | null): string {
  if (met === null) {
    return RESULT_WORDS.notCompared;
  }
  return met ? RESULT_WORDS.met : RESULT_WORDS.notMet;
}

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null | undefined; unit?: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Verdict({ met }: { met: boolean | null }) {
  const key = resultKey(met);
  return (
    <td data-check-result={key}>
      <span data-check-pill>
        <StatusIcon tone={checkTone(key)} size={16} />
        {resultWord(met)}
      </span>
    </td>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
              <th scope="col">Basis</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function sourceOr(source: string | null | undefined): string {
  return source ?? "source not declared";
}

// The route as declared: the tray, its usable dimensions, the cover, the feeders.
function RouteTable({ result }: { result: CableTrayResponse }) {
  return (
    <ValueTable title="Tray route">
      <tr data-row-key="tray_type">
        <th scope="row">Tray type</th>
        <td data-tray-type={result.tray_type}>{TRAY_TYPE_LABELS[result.tray_type]}</td>
        <td>{COVER_LABELS[result.cover]}</td>
      </tr>
      <tr data-row-key="arrangement">
        <th scope="row">Cable arrangement</th>
        <td data-arrangement={result.arrangement}>{ARRANGEMENT_LABELS[result.arrangement]}</td>
        <td data-layer-mode={result.layer_mode}>
          {result.layer_mode === "SINGLE_LAYER" ? "width rule (3.2)" : "area rule (3.1)"}
        </td>
      </tr>
      <tr data-row-key="usable_dimensions">
        <th scope="row">Usable width × depth (mm)</th>
        <td title={`${result.usable_width_mm} × ${result.usable_depth_mm} mm`}>
          {formatQuantity(result.usable_width_mm, "mm").display} ×{" "}
          {formatQuantity(result.usable_depth_mm, "mm").display}
        </td>
        <td>{sourceOr(result.dimensions_source)}</td>
      </tr>
      <tr data-row-key="usable_area_mm2">
        <th scope="row">Usable area (mm²)</th>
        <Quantity value={result.usable_area_mm2} unit="mm²" />
        <td>width × depth</td>
      </tr>
      <tr data-row-key="feeders">
        <th scope="row">Feeders carried (SLD)</th>
        <td>{result.sld_element_codes.length === 0 ? EMPTY_QUANTITY : result.sld_element_codes.join(", ")}</td>
        <td>{result.total_cables} cables in {result.rows.length} {result.rows.length === 1 ? "row" : "rows"}</td>
      </tr>
    </ValueTable>
  );
}

// The fill: the area rule or the width rule by the layer mode (sections 3.1, 3.2).
function FillTable({ result }: { result: CableTrayResponse }) {
  const single = result.layer_mode === "SINGLE_LAYER";
  return (
    <ValueTable title="Fill">
      {single ? (
        <>
          <tr data-row-key="occupied_width_mm">
            <th scope="row">Width occupied Σ D + Σ spacing (mm)</th>
            <Quantity value={result.occupied_width_mm} unit="mm" />
            <td>one declared spacing per gap; touching when none</td>
          </tr>
          <tr data-row-key="occupied_width_with_allowance_mm">
            <th scope="row">Width judged with the declared allowance (mm)</th>
            <Quantity value={result.occupied_width_with_allowance_mm} unit="mm" />
            <td>
              {result.future_allowance === null
                ? "no allowance: the present width"
                : result.future_allowance.basis === "WIDTH_AND_LOAD"
                  ? `present width × (1 + ${formatQuantity(result.future_allowance.percent, "%").display} / 100)`
                  : "allowance basis names the area, not the width: the present width"}
            </td>
          </tr>
          <tr data-row-key="remaining_width_mm">
            <th scope="row">Remaining width (mm)</th>
            <Quantity value={result.remaining_width_mm} unit="mm" />
            <td>usable width − judged width (negative when exceeded)</td>
          </tr>
          <tr data-row-key="width_ratio">
            <th scope="row">Width ratio</th>
            <Quantity value={result.width_ratio} />
            <td>occupied / usable</td>
          </tr>
          <tr data-row-key="largest_diameter_mm">
            <th scope="row">Largest diameter (mm) · depth under the cover</th>
            <Quantity value={result.largest_diameter_mm} unit="mm" />
            <td>
              {result.cover === "NONE"
                ? "no cover: depth not compared"
                : result.depth_met === null
                  ? "not compared"
                  : result.depth_met
                    ? "within the usable depth"
                    : "exceeds the usable depth"}
            </td>
          </tr>
          <tr data-row-key="fill_result">
            <th scope="row">Width condition</th>
            <Verdict met={result.width_met} />
            <td data-fill-basis={result.fill_basis}>{FILL_BASIS_LABELS[result.fill_basis]}</td>
          </tr>
        </>
      ) : (
        <>
          <tr data-row-key="occupied_area_mm2">
            <th scope="row">Occupied area Σ n π D² / 4 (mm²)</th>
            <Quantity value={result.occupied_area_mm2} unit="mm²" />
            <td>circumscribed circles of the declared diameters</td>
          </tr>
          <tr data-row-key="fill_percent">
            <th scope="row">Present fill (%)</th>
            <Quantity value={result.fill_percent} unit="%" />
            <td title={result.fill_ratio ?? undefined}>
              information · ratio {result.fill_ratio === null ? EMPTY_QUANTITY : formatQuantity(result.fill_ratio).display}
            </td>
          </tr>
          <tr data-row-key="fill_percent_with_allowance">
            <th scope="row">Fill judged with the declared allowance (%)</th>
            <Quantity value={result.fill_percent_with_allowance} unit="%" />
            <td>
              {result.future_allowance === null
                ? "no allowance: the present fill"
                : `present fill × (1 + ${formatQuantity(result.future_allowance.percent, "%").display} / 100)`}
            </td>
          </tr>
          <tr data-row-key="fill_limit">
            <th scope="row">Declared fill limit (%)</th>
            <Quantity value={result.fill_limit?.percent ?? null} unit="%" />
            <td>{result.fill_limit === null ? "not declared" : sourceOr(result.fill_limit.source)}</td>
          </tr>
          <tr data-row-key="fill_margin_percent">
            <th scope="row">Fill margin (%)</th>
            <Quantity value={result.fill_margin_percent} unit="%" />
            <td>limit − judged fill</td>
          </tr>
          <tr data-row-key="fill_result">
            <th scope="row">Fill condition</th>
            <Verdict met={result.fill_met} />
            <td data-fill-basis={result.fill_basis}>{FILL_BASIS_LABELS[result.fill_basis]}</td>
          </tr>
        </>
      )}
    </ValueTable>
  );
}

// The load: the declared masses with the allowance against the declared rating at its span.
function LoadTable({ result }: { result: CableTrayResponse }) {
  const rated = result.rated_load;
  const spacing = result.support_spacing;
  const allowance = result.future_allowance;
  return (
    <ValueTable title="Load">
      <tr data-row-key="mass_kg_per_m">
        <th scope="row">Mass of the cables Σ n m (kg/m)</th>
        <Quantity value={result.mass_kg_per_m} unit="kg/m" />
        <td>{result.mass_kg_per_m === null ? "a row's mass is not declared" : "declared masses per metre"}</td>
      </tr>
      <tr data-row-key="future_allowance">
        <th scope="row">Future allowance (%)</th>
        <Quantity value={allowance?.percent ?? null} unit="%" />
        <td>
          {allowance === null
            ? "not declared (installed cables alone)"
            : `${sourceOr(allowance.source)} · applies to ${ALLOWANCE_BASIS_LABELS[allowance.basis]}`}
        </td>
      </tr>
      <tr data-row-key="load_kg_per_m">
        <th scope="row">Distributed load (kg/m)</th>
        <Quantity value={result.load_kg_per_m} unit="kg/m" />
        <td>mass × (1 + allowance / 100)</td>
      </tr>
      <tr data-row-key="rated_load">
        <th scope="row">Rated load as declared</th>
        <td title={rated === null ? undefined : `${rated.value_kg} kg ${rated.basis}`}>
          {rated === null
            ? EMPTY_QUANTITY
            : `${formatQuantity(rated.value_kg, "kg").display} ${RATED_LOAD_BASIS_LABELS[rated.basis]}${
                rated.rated_span_m === null ? "" : ` at ${formatQuantity(rated.rated_span_m, "m").display} span`
              }`}
        </td>
        <td>{rated === null ? "not declared" : sourceOr(rated.source)}</td>
      </tr>
      <tr data-row-key="rated_load_kg_per_m">
        <th scope="row">Rated load per metre (kg/m)</th>
        <Quantity value={result.rated_load_kg_per_m} unit="kg/m" />
        <td>{rated?.basis === "PER_SPAN" ? "rating per span / rated span" : "as declared"}</td>
      </tr>
      <tr data-row-key="support_spacing">
        <th scope="row">Support spacing (m) · rated span</th>
        <td title={spacing === null ? undefined : `${spacing.value_m} m`}>
          {spacing === null ? EMPTY_QUANTITY : formatQuantity(spacing.value_m, "m").display}
          {rated?.rated_span_m ? ` · ${formatQuantity(rated.rated_span_m, "m").display}` : ""}
        </td>
        <td data-span-covered={result.span_covered === null ? "not-evaluated" : String(result.span_covered)}>
          {spacing === null
            ? "not declared"
            : result.span_covered === null
              ? `${sourceOr(spacing.source)} · rated span not declared`
              : result.span_covered
                ? `${sourceOr(spacing.source)} · within the rated span`
                : `${sourceOr(spacing.source)} · beyond the rated span`}
        </td>
      </tr>
      <tr data-row-key="load_margin">
        <th scope="row">Load margin (kg/m) · ratio</th>
        <td title={`${result.load_margin_kg_per_m ?? "—"} · ${result.load_ratio ?? "—"}`}>
          {formatQuantity(result.load_margin_kg_per_m).display} · {formatQuantity(result.load_ratio).display}
        </td>
        <td>rated − load · load / rated</td>
      </tr>
      <tr data-row-key="load_result">
        <th scope="row">Load condition</th>
        <Verdict met={result.load_met} />
        <td data-load-basis={result.load_basis}>{LOAD_BASIS_LABELS[result.load_basis]}</td>
      </tr>
    </ValueTable>
  );
}

function cableText(row: CableRowResponse): string {
  const linked = row.linked_cable_run;
  if (linked) {
    return `${linked.study_code} rev ${linked.revision_number} · ${linked.construction.toLowerCase().replace("_", "-")} ${
      linked.conductor_material === "COPPER" ? "Cu" : "Al"
    } ${linked.insulation_material}${linked.phase_area_mm2 ? ` ${formatQuantity(linked.phase_area_mm2, "mm²").display}` : ""} · ${
      linked.installation_method
    }`;
  }
  const cable = row.declared_cable;
  if (cable) {
    return `${cable.cores}-core ${cable.construction.toLowerCase().replace("_", "-")} ${
      cable.conductor_material === "COPPER" ? "Cu" : "Al"
    } ${cable.insulation_material}`;
  }
  return EMPTY_QUANTITY;
}

// One line per row: the cable, n, D, spacing, mass, the figures formed (section 9).
function RowsTable({ rows, single }: { rows: readonly CableRowResponse[]; single: boolean }) {
  return (
    <section aria-label="Cable rows">
      <h3>Cable rows</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Row</th>
              <th scope="col">Cable</th>
              <th scope="col">n</th>
              <th scope="col">D (mm)</th>
              <th scope="col">Spacing (mm)</th>
              <th scope="col">m (kg/m)</th>
              <th scope="col">{single ? "Width n D + (n−1) s (mm)" : "Area n π D² / 4 (mm²)"}</th>
              <th scope="col">Load n m (kg/m)</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.code} data-cable-row={row.code} data-row-status={row.status} data-row-kind={rowKind(row)}>
                <th scope="row">{row.code}</th>
                <td data-cable-source={row.cable_source} title={row.name}>
                  {cableText(row)}
                </td>
                <td title={row.quantity_source ?? undefined}>{row.quantity}</td>
                <td title={row.overall_diameter_mm ? `${row.overall_diameter_mm} mm · ${row.diameter_source ?? "source not declared"}` : undefined}>
                  {row.overall_diameter_mm === null ? "not declared" : formatQuantity(row.overall_diameter_mm, "mm").display}
                </td>
                <td title={row.spacing_mm ? `${row.spacing_mm} mm · ${row.spacing_source ?? "source not declared"}` : undefined}>
                  {row.spacing_mm === null ? (single ? "touching" : EMPTY_QUANTITY) : formatQuantity(row.spacing_mm, "mm").display}
                </td>
                <td title={row.mass_kg_per_m ? `${row.mass_kg_per_m} kg/m · ${row.mass_source ?? "source not declared"}` : undefined}>
                  {row.mass_kg_per_m === null ? "not declared" : formatQuantity(row.mass_kg_per_m, "kg/m").display}
                </td>
                {single ? (
                  <Quantity value={row.occupied_width_mm} unit="mm" />
                ) : (
                  <Quantity value={row.occupied_area_mm2} unit="mm²" />
                )}
                <Quantity value={row.load_kg_per_m} unit="kg/m" />
                <td data-check-result={row.status === "DESIGN_CHECK_PASSED" ? "pass" : "review"}>
                  <span data-check-pill>
                    <StatusIcon tone={checkTone(row.status === "DESIGN_CHECK_PASSED" ? "pass" : "review")} size={16} />
                    {STATUS_LABELS[row.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function CableTrayResultPanel({ result }: CableTrayResultPanelProps) {
  return (
    <article aria-label="Cable tray result">
      <header>
        <h2>Cable tray result</h2>
        <p>
          Route code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status: <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <RouteTable result={result} />
      <FillTable result={result} />
      <LoadTable result={result} />
      <RowsTable rows={result.rows} single={result.layer_mode === "SINGLE_LAYER"} />

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. No profile holds a tray table in
          this version: the fill limit, the rated load and its span, the support spacing, the
          diameters and the masses are the engineer's declared figures with their sources
          (GAP-028); the arithmetic is universal.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the tray manufacturer's data, the cable
          data sheets and every declared figure and its source before approval or issue. {METHOD_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default CableTrayResultPanel;
